import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, UserPlus } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STAGES = [
  { key: "fresh", title: "Fresh start", blurb: "Verified account, nothing submitted. Walk Care Intake → series → intake → matching from step one." },
  { key: "intake", title: "Care Intake submitted", blurb: "Account with a case waiting in your queue, so staff can verify eligibility, accommodate the series, match, and book." },
];

const Credentials = ({ result, onDismiss }) => {
  const copy = async () => { await navigator.clipboard.writeText(`${result.email}\n${result.password}`); toast.success("Copied."); };
  return (
    <div className="mt-5 border-2 border-olive-900 bg-cream-100 p-5" role="status" data-testid="demo-credentials">
      <p className="font-serif text-xl font-semibold">{result.name} is ready</p>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-ink-500">Email</dt><dd className="font-mono" data-testid="demo-email">{result.email}</dd>
        <dt className="text-ink-500">Password</dt><dd className="font-mono" data-testid="demo-password">{result.password}</dd>
        {result.case && <><dt className="text-ink-500">Case</dt><dd data-testid="demo-case">{result.case.case_code} · {result.case.state.replaceAll("_", " ")}</dd></>}
      </dl>
      <p className="help mt-3">Shown once. Creating the same demo again sets a new password. Emails to this address are simulated and appear in Communications.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={copy} data-testid="demo-copy"><Copy className="h-4 w-4" aria-hidden="true" /> Copy Sign-In</button>
        <a href="/sign-in" target="_blank" rel="noopener noreferrer" className="btn-quiet" data-testid="demo-open-sign-in">Open Sign In</a>
        <button type="button" className="btn-quiet" onClick={onDismiss} data-testid="demo-dismiss">Hide</button>
      </div>
    </div>
  );
};

export const DemoMembers = () => {
  const qc = useQueryClient();
  const [result, setResult] = useState(null);
  const { data } = useQuery({ queryKey: ["admin", "demo"], queryFn: async () => (await api.get("/admin/demo/members")).data });
  const create = useMutation({
    mutationFn: (stage) => api.post("/admin/demo/members", { stage }),
    onSuccess: (r) => { setResult(r.data); qc.invalidateQueries({ queryKey: ["admin", "demo"] }); toast.success("Demo member ready."); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (!data) return null;
  return (
    <section className="mt-12" aria-labelledby="demo-heading" data-testid="demo-members">
      <h2 id="demo-heading" className="text-2xl">Demo Members</h2>
      <p className="mt-2 max-w-[65ch] text-ink-500">Fictional care recipients so you can see the member side without touching real records. Both use an <span className="font-mono">@example.com</span> address, so nothing is ever emailed. Only a System Admin sees this panel.</p>
      {!data.enabled && <p className="mt-3 border-l-4 border-terracotta-600 bg-cream-100 p-3 text-sm" data-testid="demo-disabled">Turn on the <strong>Demo Data</strong> feature flag above to create demo members.</p>}
      <ul className="mt-5 grid gap-4 md:grid-cols-2">
        {STAGES.map((s) => {
          const row = data.members.find((m) => m.stage === s.key);
          return (
            <li key={s.key} className="surface p-5" data-testid={`demo-stage-${s.key}`}>
              <p className="font-serif text-xl font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-ink-500">{s.blurb}</p>
              <p className="mt-3 text-sm" data-testid={`demo-status-${s.key}`}>
                {row?.exists ? <>Exists · <span className="font-mono">{row.email}</span>{row.case_code ? ` · case ${row.case_code} (${row.case_state.replaceAll("_", " ")})` : " · no case yet"}</> : "Not created yet"}
              </p>
              <button type="button" className="btn-primary mt-4 inline-flex items-center gap-2" disabled={!data.enabled || create.isPending} onClick={() => create.mutate(s.key)} data-testid={`demo-create-${s.key}`}>
                <UserPlus className="h-4 w-4" aria-hidden="true" /> {row?.exists ? "Reset Password" : "Create"}
              </button>
            </li>
          );
        })}
      </ul>
      {result && <Credentials result={result} onDismiss={() => setResult(null)} />}
    </section>
  );
};
